import FlagOutlined from "@mui/icons-material/FlagOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, IconButton, type SxProps, type Theme, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../components/UIToolkit/Badges/MetaChip";
import { type SystemChipItem, SystemChips } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../../components/UIToolkit/molecules/SettingsSection";
import { SystemDataGrid, type SystemGridColDef } from "../../../../components/UIToolkit/Tables/SystemDataGrid";
import { useAuth } from "../../../../contexts/AuthProvider";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { backendService } from "../../../../services/backend.service";
import { featureFlagsAdminSortingKey } from "../../../../services/LocalStorageService";
import { INK, MOTION, STATUS_BADGE } from "../../../../theme/tokens";
import type { MultiSortOption } from "../../../../utils/multiColumnSorting";
import { SIDE_LAID_GROUND_SX } from "./ratesConsts";
import type { FlagAdminMetadata, FlagDefaultSource, FlagTargetingCaveat } from "./types";

/** Curated display names, keyed by flag key. The rows come from LaunchDarkly rather than from this
 * constant — a flag nobody added to it must still appear — so it is only consulted for a nicer label
 * than the key, and flags missing from it fall back to a humanized key. */
const CURATED_FLAG_NAMES: Record<string, string> = Object.fromEntries(
	Object.entries(FEATURE_FLAGS).map(([constantName, flagKey]) => [
		flagKey,
		// Split the constant's Pascal case into words.
		constantName.replace(/([A-Z])/g, " $1").trim(),
	]),
);

const humanizeFlagKey = (flagKey: string): string => {
	const words = flagKey.replace(/[_-]+/g, " ").trim();
	return words.charAt(0).toUpperCase() + words.slice(1);
};

const flagDisplayName = (flagKey: string): string => CURATED_FLAG_NAMES[flagKey] ?? humanizeFlagKey(flagKey);

/** Only a real boolean gets the On/Off treatment. `typeof` rather than a truthiness check on purpose:
 * a multivariate flag serving `0`, `""` or `{}` is a value to show, not an "Off". */
const isBooleanValue = (value: unknown): value is boolean => typeof value === "boolean";

/** Which subset of the flags the table is showing. The table is an inspector, so the interesting
 * question is rarely "all 81" — it is one of these slices. */
type FlagFilter = "all" | "overridden" | "on" | "off" | "caveats" | "multivariate";

type FlagRow = FlagAdminMetadata & {
	flagKey: string;
	displayName: string;
	/** This department's value differs from what LaunchDarkly serves when nothing matches. The
	 * handful of rows where this is true is the reason anyone opens this tab. */
	isOverridden: boolean;
	/** Pre-rendered so search, sort and the cell renderer all read the same string. */
	valueText: string;
	defaultText: string;
};

const CAVEAT_LABEL_KEY: Record<FlagTargetingCaveat["kind"], string> = {
	non_department_attribute: "department.config.featureFlags.caveat.attributes",
	segment: "department.config.featureFlags.caveat.segment",
	individual_targets: "department.config.featureFlags.caveat.individualTargets",
	prerequisite: "department.config.featureFlags.caveat.prerequisite",
	percentage_rollout: "department.config.featureFlags.caveat.rollout",
};

const DEFAULT_SOURCE_LABEL_KEY: Record<FlagDefaultSource, string> = {
	off_variation: "department.config.featureFlags.defaultSource.offVariation",
	fallthrough: "department.config.featureFlags.defaultSource.fallthrough",
	percentage_rollout: "department.config.featureFlags.defaultSource.rollout",
	code_default: "department.config.featureFlags.defaultSource.codeDefault",
};

// Row accent for an overridden flag: a left bar plus a faint wash, so the few rows that differ from
// their default are findable by eye in a long list without reading a single cell.
const OVERRIDDEN_ROW_CLASS = "feature-flag-row--overridden";
const OVERRIDDEN_ACCENT_WIDTH_PX = 3;

// The LaunchDarkly link is on every row, so at full strength it becomes 80 lines of repeated icon.
// Resting it at a low opacity and lifting it on row hover / keyboard focus keeps the affordance
// without the texture — `focus-within` is what keeps it reachable without a pointer.
const LINK_CLASS = "feature-flag-link";
const LINK_RESTING_OPACITY = 0.35;

const gridSx: SxProps<Theme> = {
	[`& .${OVERRIDDEN_ROW_CLASS}`]: {
		backgroundColor: STATUS_BADGE.WARN_BG,
		boxShadow: `inset ${OVERRIDDEN_ACCENT_WIDTH_PX}px 0 0 0 ${STATUS_BADGE.WARN_INK}`,
	},
	[`& .${LINK_CLASS}`]: {
		opacity: LINK_RESTING_OPACITY,
		transition: `opacity ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
	},
	[`& .MuiDataGrid-row:hover .${LINK_CLASS}, & .MuiDataGrid-row:focus-within .${LINK_CLASS}`]: { opacity: 1 },
	"@media (prefers-reduced-motion: reduce)": {
		[`& .${LINK_CLASS}`]: { transition: "none" },
	},
};

/** Compared as JSON so a multivariate object is judged by content, not identity. */
const valuesDiffer = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);

export const FeatureFlagsTabContent: FC = () => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	// The endpoint is support-gated server-side; disabling the query keeps non-support users from
	// firing a call that can only 403, exactly as useShiftPunches does.
	const isSupport = !!userInfo?.support;
	const [filter, setFilter] = useState<FlagFilter>("all");

	const {
		data: metadata,
		isLoading,
		error,
	} = useQuery({
		queryKey: ["feature-flags-admin-metadata", departmentId],
		queryFn: async () => (await backendService.getFeatureFlagsAdminMetadata(departmentId)).data,
		enabled: isSupport && !!departmentId,
	});

	const formatValue = useCallback(
		(value: unknown): string => {
			if (value === null || value === undefined) {
				return t("department.config.featureFlags.value.none");
			}
			if (isBooleanValue(value)) {
				return value ? t("department.config.featureFlags.status.on") : t("department.config.featureFlags.status.off");
			}
			if (typeof value === "object") {
				return JSON.stringify(value);
			}
			return String(value);
		},
		[t],
	);

	const rows = useMemo<FlagRow[]>(
		() =>
			Object.entries(metadata ?? {})
				.map(([flagKey, flagMetadata]) => ({
					...flagMetadata,
					flagKey,
					displayName: flagDisplayName(flagKey),
					// A percentage rollout has no single default to differ from, so calling it an override
					// would flag every rollout as a department-specific decision it isn't.
					isOverridden: flagMetadata.default_source !== "percentage_rollout" && valuesDiffer(flagMetadata.value, flagMetadata.default_value),
					valueText: formatValue(flagMetadata.value),
					defaultText: formatValue(flagMetadata.default_value),
				}))
				.sort((a, b) => a.displayName.localeCompare(b.displayName)),
		[metadata, formatValue],
	);

	const counts = useMemo(
		() => ({
			all: rows.length,
			overridden: rows.filter((row) => row.isOverridden).length,
			on: rows.filter((row) => row.value === true).length,
			off: rows.filter((row) => row.value === false).length,
			caveats: rows.filter((row) => row.caveats.length > 0).length,
			multivariate: rows.filter((row) => !isBooleanValue(row.value)).length,
		}),
		[rows],
	);

	const visibleRows = useMemo(() => {
		switch (filter) {
			case "overridden":
				return rows.filter((row) => row.isOverridden);
			case "on":
				return rows.filter((row) => row.value === true);
			case "off":
				return rows.filter((row) => row.value === false);
			case "caveats":
				return rows.filter((row) => row.caveats.length > 0);
			case "multivariate":
				return rows.filter((row) => !isBooleanValue(row.value));
			case "all":
				return rows;
		}
	}, [rows, filter]);

	const filterItems = useMemo<SystemChipItem<FlagFilter>[]>(
		() => [
			{ value: "all", label: t("department.config.featureFlags.filter.all", { total: counts.all }) },
			{ value: "overridden", label: t("department.config.featureFlags.filter.overridden", { total: counts.overridden }), disabled: counts.overridden === 0 },
			{ value: "on", label: t("department.config.featureFlags.filter.on", { total: counts.on }) },
			{ value: "off", label: t("department.config.featureFlags.filter.off", { total: counts.off }) },
			{ value: "caveats", label: t("department.config.featureFlags.filter.caveats", { total: counts.caveats }), disabled: counts.caveats === 0 },
			{
				value: "multivariate",
				label: t("department.config.featureFlags.filter.multivariate", { total: counts.multivariate }),
				disabled: counts.multivariate === 0,
			},
		],
		[t, counts],
	);

	const caveatTooltip = useCallback(
		(caveats: FlagTargetingCaveat[]): string =>
			[
				t("department.config.featureFlags.caveat.intro"),
				...caveats.map((caveat) => t(CAVEAT_LABEL_KEY[caveat.kind], { details: caveat.details.join(", ") })),
			].join("\n"),
		[t],
	);

	const sortingOptions = useMemo<MultiSortOption<FlagRow>[]>(
		() => [
			{
				label: t("department.config.featureFlags.sort.overriddenFirst"),
				sortColumns: ["isOverridden"],
				// Ties fall back to the name so the "everything else" block below the overrides stays
				// alphabetical instead of arriving in object-key order.
				customSortFunction: (a, b) => {
					if (a.isOverridden !== b.isOverridden) return a.isOverridden ? -1 : 1;
					return Math.sign(a.displayName.localeCompare(b.displayName)) as 1 | 0 | -1;
				},
			},
			{ label: t("department.config.featureFlags.col.name"), sortColumns: ["displayName"] },
			{ label: t("department.config.featureFlags.col.status"), sortColumns: ["valueText"] },
		],
		[t],
	);

	const columns = useMemo<SystemGridColDef<FlagRow>[]>(
		() => [
			{
				field: "displayName",
				headerName: t("department.config.featureFlags.col.name"),
				flex: 2,
				minWidth: 240,
				display: "flex",
				renderCell: (params) => (
					<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0, py: 0.5 }}>
						<Box sx={{ fontWeight: 500, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis" }}>{params.row.displayName}</Box>
						<Box sx={{ fontFamily: "monospace", fontSize: "0.72rem", color: INK.MUTED, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis" }}>
							{params.row.flagKey}
						</Box>
					</Box>
				),
			},
			{
				field: "valueText",
				headerName: t("department.config.featureFlags.col.status"),
				width: 210,
				display: "flex",
				renderCell: (params) => (
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
						{isBooleanValue(params.row.value) ? (
							<MetaChip label={params.row.valueText} tone={params.row.value ? "success" : "neutral"} dataTestId={`flag-status-${params.row.flagKey}`} />
						) : (
							// Multivariate values are JSON and routinely wider than the column, so the full
							// string lives in a tooltip rather than being clipped with no way to read it.
							<Tooltip title={params.row.valueText}>
								<Box
									sx={{ fontFamily: "monospace", fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
									data-testid={`flag-status-${params.row.flagKey}`}
								>
									{params.row.valueText}
								</Box>
							</Tooltip>
						)}
						{params.row.caveats.length > 0 && (
							<Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{caveatTooltip(params.row.caveats)}</Box>}>
								{/* Focusable: the icon carries no meaning without its tooltip, so keyboard users
								    need a way to reach it. */}
								<WarningAmberIcon
									color="warning"
									fontSize="small"
									tabIndex={0}
									role="img"
									aria-label={t("department.config.featureFlags.caveat.intro")}
									data-testid={`flag-warning-${params.row.flagKey}`}
								/>
							</Tooltip>
						)}
					</Box>
				),
			},
			{
				field: "defaultText",
				headerName: t("department.config.featureFlags.col.default"),
				width: 190,
				display: "flex",
				renderCell: (params) => (
					<Tooltip
						title={
							params.row.isOverridden
								? `${t("department.config.featureFlags.overridden.tooltip")} ${t(DEFAULT_SOURCE_LABEL_KEY[params.row.default_source])}`
								: t(DEFAULT_SOURCE_LABEL_KEY[params.row.default_source])
						}
					>
						<Box
							component="span"
							data-testid={`flag-default-${params.row.flagKey}`}
							sx={{
								fontSize: "0.78rem",
								fontFamily: isBooleanValue(params.row.default_value) ? undefined : "monospace",
								// Muted when it merely repeats the value (most rows); ink-forward only where it
								// carries news — that this department is being served something else.
								color: params.row.isOverridden ? STATUS_BADGE.AMBER_INK : INK.DISABLED,
								fontWeight: params.row.isOverridden ? 600 : 400,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
						>
							{params.row.defaultText}
						</Box>
					</Tooltip>
				),
			},
			{
				field: "ld_link",
				headerName: t("department.config.featureFlags.col.link"),
				width: 90,
				align: "center",
				headerAlign: "center",
				sortable: false,
				display: "flex",
				renderCell: (params) =>
					params.row.ld_link ? (
						<IconButton
							className={LINK_CLASS}
							component="a"
							href={params.row.ld_link}
							target="_blank"
							rel="noopener noreferrer"
							size="small"
							aria-label={t("department.config.featureFlags.link.aria")}
							data-testid={`flag-link-${params.row.flagKey}`}
						>
							<OpenInNewIcon fontSize="small" />
						</IconButton>
					) : null,
			},
		],
		[t, caveatTooltip],
	);

	const content = () => {
		if (!isSupport) {
			return <EmptyState icon={<FlagOutlined />} title={t("department.config.featureFlags.supportOnly")} dataTestId="dept-config-feature-flags-support-only" />;
		}

		return (
			<SystemDataGrid<FlagRow>
				rows={visibleRows}
				columns={columns}
				getRowId={(row) => row.flagKey}
				searchableColumns={["displayName", "flagKey", "valueText"]}
				searchPlaceholder={t("department.config.featureFlags.search.placeholder")}
				sortingOptions={sortingOptions}
				defaultSortState={[{ sortColumns: ["isOverridden"], sortOrder: "asc" }]}
				localStorageKey={featureFlagsAdminSortingKey}
				loading={isLoading}
				error={!!error}
				errorMessage={t("department.config.featureFlags.error.hint")}
				leadingToolbarNodes={
					<SystemChips<FlagFilter>
						variant="filled"
						items={filterItems}
						selectedItems={filterItems.filter((item) => item.value === filter)}
						onChange={(item, selected) => setFilter(selected ? item.value : "all")}
						getItemDataTestId={(item) => `feature-flags-filter-${item.value}`}
						dataTestId="feature-flags-filter"
					/>
				}
				searchWidth={220}
				containedHeight={{ minHeight: 320, maxHeight: 620 }}
				dataGridProps={{ getRowClassName: (params) => (params.row.isOverridden ? OVERRIDDEN_ROW_CLASS : "") }}
				emptyState={
					<EmptyState
						icon={<FlagOutlined />}
						title={t(rows.length === 0 ? "department.config.featureFlags.empty" : "department.config.featureFlags.filter.empty.title")}
						hint={rows.length === 0 ? undefined : t("department.config.featureFlags.filter.empty.hint")}
						dataTestId="dept-config-feature-flags-empty"
					/>
				}
				sx={gridSx}
				dataTestId="dept-config-feature-flags-grid"
			/>
		);
	};

	return (
		<Box component="div" sx={SIDE_LAID_GROUND_SX}>
			<SettingsSection
				headerPlacement="side"
				icon={<FlagOutlined />}
				title={t("department.config.featureFlags.section.title")}
				description={t("department.config.featureFlags.section.description")}
				layout="stack"
				contentMaxWidth="none"
				divider
				dataTestId="dept-config-feature-flags"
			>
				{content()}
			</SettingsSection>
		</Box>
	);
};
